import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import './Contact.css';

// Composant pour les particules - Réutilisé et adapté depuis Hero.js
const ContactParticles = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    // Référence au canvas
    const canvas = canvasRef.current;
    if (!canvas) return; // Sortir si le canvas n'existe pas
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // Sortir si le contexte n'existe pas
    
    // Pour suivre si le composant est monté
    let isMounted = true;
    
    // Ajuster la taille du canvas à la section
    const resizeCanvas = () => {
      if (!canvas || !isMounted) return;
      
      const parentElement = canvas.parentElement;
      if (parentElement) {
        canvas.width = parentElement.offsetWidth;
        canvas.height = parentElement.offsetHeight;
      }
    };
    
    // Appliquer le redimensionnement initial
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Configuration des particules
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 8, 150);
    
    const mouse = {
      x: undefined,
      y: undefined,
      radius: 100
    };
    
    // Capturer la position de la souris
    const handleMouseMove = (event) => {
      if (!canvas || !isMounted) return;
      
      const rect = canvas.getBoundingClientRect();
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    // Classe pour les particules
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        
        // Taille variable pour plus de dynamisme
        this.size = Math.random() * 2.5 + 0.8;
        this.baseSize = this.size;
        
        // Vitesse et direction
        this.speedX = (Math.random() * 1 - 0.5) * 0.6;
        this.speedY = (Math.random() * 1 - 0.5) * 0.6;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        
        // Animation et apparence
        this.pulsing = Math.random() * 0.03 + 0.01;
        this.colorAngle = Math.random() * 360;
        this.colorSpeed = Math.random() * 0.3 + 0.1;
        this.alpha = Math.random() * 0.4 + 0.3;
        this.glow = Math.random() * 6 + 2;
        this.hueOffset = Math.random() * 20 - 10;
      }
      
      update() {
        if (!isMounted) return;
        
        // Mouvement de base
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Animation pulsante et changement de couleur
        this.colorAngle += this.colorSpeed;
        if (this.colorAngle > 360) this.colorAngle = 0;
        
        // Effet respirant
        this.size = this.baseSize + Math.sin(Date.now() * this.pulsing) * (this.baseSize * 0.3);
        
        // Interaction avec la souris
        if (mouse.x) {
          const mdx = mouse.x - this.x;
          const mdy = mouse.y - this.y;
          const mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);
          
          if (mouseDistance < mouse.radius) {
            const angle = Math.atan2(mdy, mdx);
            const force = (mouse.radius - mouseDistance) / mouse.radius;
            
            // Effet de répulsion
            this.speedX -= Math.cos(angle) * force * 0.2;
            this.speedY -= Math.sin(angle) * force * 0.2;
            
            // Augmenter l'opacité et la taille à l'approche de la souris
            this.alpha = Math.min(this.alpha + 0.2, 0.9);
            this.size = this.baseSize * (1 + force * 0.9);
          } else {
            // Revenir progressivement à la vitesse et opacité de base
            this.speedX = this.speedX * 0.97 + this.baseSpeedX * 0.03;
            this.speedY = this.speedY * 0.97 + this.baseSpeedY * 0.03;
            this.alpha = Math.max(this.alpha * 0.98, Math.random() * 0.4 + 0.3);
            this.size = Math.max(this.size * 0.98, this.baseSize);
          }
        }
        
        // Limiter la vitesse
        const maxSpeed = 1.5;
        const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
        if (currentSpeed > maxSpeed) {
          this.speedX = (this.speedX / currentSpeed) * maxSpeed;
          this.speedY = (this.speedY / currentSpeed) * maxSpeed;
        }
        
        // Friction légère
        this.speedX *= 0.99;
        this.speedY *= 0.99;
        
        // Rebond aux limites de l'écran seulement si le canvas existe toujours
        if (canvas && canvas.width && canvas.height) {
          const margin = 10; // Marge pour éviter que les particules ne disparaissent complètement
          
          if (this.x < margin) {
            this.x = margin;
            this.speedX *= -0.8;
          } else if (this.x > canvas.width - margin) {
            this.x = canvas.width - margin;
            this.speedX *= -0.8;
          }
          
          if (this.y < margin) {
            this.y = margin;
            this.speedY *= -0.8;
          } else if (this.y > canvas.height - margin) {
            this.y = canvas.height - margin;
            this.speedY *= -0.8;
          }
        }
      }
      
      draw() {
        if (!ctx || !isMounted) return;
        
        // Centre plus défini avec moins de halo
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.7, 0, Math.PI * 2);
        
        // Couleur magenta avec variation
        const hue = Math.abs(Math.sin(this.colorAngle) * 30) + 290 + this.hueOffset;
        const saturation = 100;
        const lightness = 68 + Math.sin(this.colorAngle * 0.5) * 8;
        
        // Centre plus opaque pour mieux définir la particule
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 1.2})`;
        ctx.fill();
        
        // Effet de halo lumineux
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size + this.glow
        );
        
        gradient.addColorStop(0, `hsla(${hue}, ${saturation}%, ${lightness + 10}%, ${this.alpha * 0.9})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${saturation}%, ${lightness}%, ${this.alpha * 0.6})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${saturation}%, ${lightness - 10}%, ${this.alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${saturation}%, ${lightness - 20}%, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size + this.glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Créer les particules
    const init = () => {
      if (!isMounted || !canvas) return;
      
      particlesArray.length = 0;
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    
    // Initialiser les particules
    init();
    
    // Animer les particules
    let animationFrameId = null;
    
    const animate = () => {
      if (!canvas || !ctx || !isMounted) {
        return;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particlesArray.length; i++) {
        if (particlesArray[i] && isMounted) {
          particlesArray[i].update();
          particlesArray[i].draw();
        }
      }
      
      if (isMounted) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };
    
    // Lancer l'animation
    animate();
    
    // Nettoyage
    return () => {
      isMounted = false;
      
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);
  
  return (
    <canvas ref={canvasRef} className="contact-particles" />
  );
};

function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(null);
  
  // Assurer que la page s'affiche tout en haut au chargement
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Mettre à jour le titre de la page
    document.title = `${t('contact')} - Quizzer`;
    
    // Mettre à jour la meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('contactIntro'));
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = t('contactIntro');
      document.head.appendChild(meta);
    }
  }, [t]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    // Simulation d'envoi de formulaire (à remplacer par une vraie API)
    try {
      // Attendre 1.5 secondes pour simuler l'envoi
      await new Promise(resolve => setTimeout(resolve, 1500));
      setFormStatus('success');
      
      // Réinitialiser le formulaire après 3 secondes
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setFormStatus(null);
      }, 3000);
    } catch (error) {
      setFormStatus('error');
    }
  };
  
  return (
    <div className="contact-page">
      <Header />
      <main className="main-content">
        <section className="contact-section">
          <ContactParticles />
          <div className="container">
            <div className="contact-container fade-in">
              <div className="contact-header">
                <h1 className="contact-title">{t('contactTitle')}</h1>
                <p className="contact-intro">{t('contactIntro')}</p>
              </div>
              
              <div className="contact-form-container">
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">{t('contactName')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">{t('contactEmail')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subject">{t('contactSubject')}</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled>--</option>
                      <option value="suggestion">{t('contactSubjectSuggestion')}</option>
                      <option value="category">{t('contactSubjectCategory')}</option>
                      <option value="bug">{t('contactSubjectBug')}</option>
                      <option value="other">{t('contactSubjectOther')}</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">{t('contactMessage')}</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                    ></textarea>
                  </div>
                  
                  <div className="form-status">
                    {formStatus === 'success' && (
                      <div className="status-message success">{t('contactSuccess')}</div>
                    )}
                    {formStatus === 'error' && (
                      <div className="status-message error">{t('contactError')}</div>
                    )}
                  </div>
                  
                  <button 
                    type="submit" 
                    className={`submit-button ${formStatus === 'sending' ? 'sending' : ''}`}
                    disabled={formStatus === 'sending'}
                  >
                    {formStatus === 'sending' ? (
                      <div className="spinner"></div>
                    ) : t('contactSend')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Contact; 